import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Download, Lock, Smartphone, Sparkles, Zap } from "lucide-react";
import { api } from "../../lib/api";
import { ProductCard } from "../../components/site/ProductCard";
import { EmptyState } from "../../components/site/Bits";

const HERO = "https://images.unsplash.com/photo-1590887545563-b6294b091b02?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const FEATURES = [
  { icon: Zap, title: "Instant delivery", text: "Your product unlocks in your account the moment your payment is verified.", span: "lg:col-span-2" },
  { icon: Lock, title: "Verified payments", text: "Every transaction is confirmed server-side with theteller before access is granted." },
  { icon: Smartphone, title: "Mobile Money & cards", text: "Pay with MTN MoMo, Telecel Cash, AT Money, Visa or Mastercard." },
  { icon: Download, title: "Always in My Purchases", text: "Re-open or re-download what you bought, any time, from any device.", span: "lg:col-span-2" },
];

const Hero = () => (
  <section className="relative overflow-hidden grain">
    <div className="grid-lines absolute inset-0 opacity-60" />
    <div className="absolute -left-40 top-0 h-[500px] w-[500px] rounded-full bg-emerald-500/10 blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-16 lg:grid-cols-12 lg:px-8 lg:pb-28 lg:pt-24">
      <div className="lg:col-span-6">
        <span className="rise inline-flex items-center gap-2 rounded-full border border-amber-400/25 bg-amber-400/10 px-3 py-1.5 text-xs font-semibold text-amber-300">
          <Sparkles size={13} /> Ghana's digital goods store · Prices in GH₵
        </span>
        <h1 className="rise d1 mt-6 text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
          Digital products,<br />delivered the moment <span className="text-emerald-400">you pay.</span>
        </h1>
        <p className="rise d2 mt-6 max-w-lg text-base leading-relaxed text-slate-300">
          Raphanex is an online store for quality digital products — e-books, templates, software tools, courses and more.
          Buy securely with Mobile Money or card and get instant access in your account.
        </p>
        <div className="rise d3 mt-9 flex flex-wrap gap-3">
          <Link to="/products" className="btn-primary px-6" data-testid="hero-browse-btn">Browse products <ArrowRight size={16} /></Link>
          <Link to="/register" className="btn-ghost px-6" data-testid="hero-register-btn">Create free account</Link>
        </div>
        <div className="rise d4 mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-400">
          {["Secure checkout", "Instant access", "Local payment methods"].map((t) => (
            <span key={t} className="inline-flex items-center gap-2"><BadgeCheck size={16} className="text-emerald-400" /> {t}</span>
          ))}
        </div>
      </div>
      <div className="rise d2 relative lg:col-span-6">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_-40px_rgba(16,185,129,0.35)]">
          <img src={HERO} alt="Digital products on devices" className="aspect-[5/4] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f17] via-transparent to-transparent" />
        </div>
        <div className="glass absolute -bottom-6 left-6 rounded-2xl p-4 sm:left-10">
          <p className="text-xs text-slate-400">Payment verified</p>
          <p className="mt-1 flex items-center gap-2 font-semibold text-white"><BadgeCheck size={18} className="text-emerald-400" /> Access granted instantly</p>
        </div>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
    <p className="eyebrow">Why Raphanex</p>
    <h2 className="mt-3 max-w-xl text-3xl font-bold text-white sm:text-4xl">A store built for trust and speed.</h2>
    <div className="mt-12 grid gap-5 lg:grid-cols-3">
      {FEATURES.map((f) => (
        <div key={f.title} className={`card-rx card-hover p-8 ${f.span || ""}`}>
          <f.icon className="text-emerald-400" size={26} />
          <h3 className="mt-6 text-xl font-semibold text-white">{f.title}</h3>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-400">{f.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const Latest = () => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/products").then((r) => setItems(r.data.slice(0, 6))).catch(() => setItems([])); }, []);
  return (
    <section className="border-y border-white/[0.05] bg-[#080b12]">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Catalogue</p>
            <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">Latest digital products</h2>
          </div>
          <Link to="/products" className="hidden text-sm font-semibold text-emerald-300 hover:text-emerald-200 sm:inline-flex sm:items-center sm:gap-1" data-testid="home-all-products-link">
            View all <ArrowRight size={15} />
          </Link>
        </div>
        <div className="mt-10">
          {items && items.length === 0 ? (
            <EmptyState testId="home-products-empty" title="Our shelves are being stocked" text="New digital products are being added. Create an account to be ready when they launch."
              action={<Link to="/register" className="btn-primary">Create account</Link>} />
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {(items || []).map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const Steps = () => (
  <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <p className="eyebrow">How it works</p>
        <h2 className="mt-3 text-3xl font-bold text-white sm:text-4xl">Four steps from browse to download.</h2>
        <Link to="/how-it-works" className="btn-ghost mt-8" data-testid="home-how-link">Learn more <ArrowRight size={15} /></Link>
      </div>
      <ol className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
        {["Create your free account with your phone number.", "Choose a digital product from the catalogue.", "Pay securely through theteller with MoMo or card.", "Access it instantly under My Purchases."].map((s, i) => (
          <li key={s} className="card-rx p-7">
            <span className="font-mono-rx text-sm text-amber-400">0{i + 1}</span>
            <p className="mt-4 text-base font-medium text-slate-100">{s}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const Cta = () => (
  <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
    <div className="relative overflow-hidden rounded-3xl border border-emerald-500/20 bg-[#0c1a16] p-10 sm:p-14">
      <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-emerald-500/20 blur-[90px]" />
      <h2 className="relative max-w-xl text-3xl font-bold text-white sm:text-4xl">Ready to get started?</h2>
      <p className="relative mt-3 max-w-lg text-slate-300">Open your free Raphanex account in under a minute.</p>
      <Link to="/register" className="btn-primary relative mt-8" data-testid="cta-register-btn">Create account <ArrowRight size={16} /></Link>
    </div>
  </section>
);

export default function Home() {
  return (<><Hero /><Features /><Latest /><Steps /><Cta /></>);
}
